import { Component, computed, inject, input, model, signal, viewChild } from '@angular/core';
import { SafeResourceUrl } from '@angular/platform-browser';
import { ColDef, GridOptions } from 'ag-grid-community';
import { injectQueryParams } from 'ngxtension/inject-query-params';
import { finalize } from 'rxjs';
import { PanModalComponent } from '../../../../components/pan-modal/pan-modal.component';
import { defaultTableDefOptions } from '../../../../helpers';
import { BranchService } from '../../../../service/branch.service';
import { EdgeDeviceService } from '../../../../service/edge-device.service';
import { LocationService } from '../../../../service/location.service';
import { NotifyService } from '../../../../service/notify.service';
import { Branch, Location, LocationCamera, LocationResponse } from '../../../../types';
import { EdgeDevice } from '../../../../types/edge-device';
import { CellInstallerDashboardShoppersenseComponent } from '../cell-installer-dashboard-shoppersense/cell-installer-dashboard-shoppersense.component';

@Component({
  selector: 'app-installer-dashboard-table-shoppersense',
  standalone: false,
  templateUrl: './installer-dashboard-table-shoppersense.component.html',
  styleUrl: './installer-dashboard-table-shoppersense.component.scss',
})
export class InstallerDashboardTableShoppersenseComponent {
  private branchService = inject(BranchService);
  private shoppersenseService = inject(EdgeDeviceService);
  private notifyService = inject(NotifyService);
  private locationService = inject(LocationService);

  public branchId = input.required<number>();
  public shoppersenses = model.required<EdgeDevice[]>();

  public queryParams = injectQueryParams();

  public newShoppersenseModal = viewChild<PanModalComponent>('newShoppersenseModal');

  public isLoadingLocations = signal<boolean>(false);
  public locations = signal<LocationResponse[]>([]);

  public isLoadingCameras = signal<boolean>(false);
  public cameras = signal<LocationCamera[]>([]);

  public newShoppersenseSerialNumber = signal<string>('');
  public newShoppersenseLocation = signal<Location | undefined>(undefined);
  public newShoppersenseBranch = signal<Branch | undefined>(undefined);
  public newShoppersenseCamera = signal<LocationCamera | undefined>(undefined);

  public viewCameraUrl = signal<SafeResourceUrl>('');

  public deviceId = computed(() => this.queryParams().device_id);

  public readonly shoppersenseColumns: ColDef<EdgeDevice>[] = [
    {
      field: 'serial_number',
      headerName: 'Serial Number',
      cellDataType: 'text',
      minWidth: 200,
      filter: 'agTextColumnFilter',
      sortable: true,
      sort: 'asc',
    },
    {
      field: 'branch.name',
      headerName: 'Branch',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      field: 'camera.location.name',
      headerName: 'Location',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
      tooltipField: 'camera.location.name',
    },
    {
      field: 'camera.camera_name',
      headerName: 'Camera',
      cellDataType: 'text',
      filter: 'agTextColumnFilter',
      sortable: true,
    },
    {
      headerName: '',
      suppressColumnsToolPanel: true,
      suppressKeyboardEvent: () => true,
      sortable: false,
      width: 280,
      maxWidth: 280,
      cellClass: ['inline-flex', 'items-center', 'justify-end'],
      cellRenderer: CellInstallerDashboardShoppersenseComponent,
      cellRendererParams: {
        branchId: () => this.branchId(),
        deleteCallback: (element: EdgeDevice) => this.removeShoppersense(element),
      },
    },
  ];

  public readonly shoppersenseTable: GridOptions<EdgeDevice> = {
    ...defaultTableDefOptions(),
    pagination: true,
    paginationPageSize: 20,
    paginationPageSizeSelector: false,
    stopEditingWhenCellsLoseFocus: true,
  };

  public loadLocations() {
    if (!this.newShoppersenseBranch()) return;

    this.isLoadingLocations.set(true);
    this.locationService
      .getAllLocations({ branch_ids: [this.newShoppersenseBranch()?.id] })
      .pipe(finalize(() => this.isLoadingLocations.set(false)))
      .subscribe((locationRes) => {
        this.locations.set(locationRes.items);
      });
  }

  public loadCameras() {
    if (!this.newShoppersenseBranch() || !this.newShoppersenseLocation()) return;

    this.isLoadingCameras.set(true);
    this.branchService
      .getBranchAvailableCamerasForEdgeDevices(this.newShoppersenseBranch()?.id, this.newShoppersenseLocation()?.id)
      .pipe(finalize(() => this.isLoadingCameras.set(false)))
      .subscribe((cameraRes) => {
        this.cameras.set(cameraRes.items);
      });
  }

  public addShoppersense() {
    this.shoppersenseService
      .connectEdgeDeviceToCamera(this.deviceId() ?? this.newShoppersenseSerialNumber(), {
        branch_id: this.newShoppersenseBranch()!.id,
        location_id: this.newShoppersenseLocation()!.id,
        location_camera_id: this.newShoppersenseCamera()?.id,
        provider_camera_id: this.newShoppersenseCamera()?.provider_camera_id,
      })
      .subscribe((device) => {
        this.shoppersenses.update((shoppersenses) => [...shoppersenses, device]);
        this.notifyService.notify('ShopperSense added successfully!');
      });
  }

  public removeShoppersense(device: EdgeDevice) {
    this.shoppersenseService.disconnectEdgeDeviceFromCamera(device.id).subscribe((removedDevice) => {
      this.shoppersenses.update((shoppersenses) => shoppersenses.filter((s) => s.id !== removedDevice.id));
      this.notifyService.notify('ShopperSense removed successfully!');
    });
  }

  public loadCameraSnapshot(camera: LocationCamera) {
    this.viewCameraUrl.set(undefined);
    this.branchService.getCameraSnapshot(this.branchId(), camera.provider_branch_id, camera.provider_camera_id).subscribe({
      next: (blob) => {
        this.viewCameraUrl.set(URL.createObjectURL(blob));
      },
      error: () => this.viewCameraUrl.set('error'),
    });
  }
}
